import { useMemo, useRef, useState } from "react";
import type { Cut, WorkResponse } from "@studio/shared";
import { InlineSpinner } from "../components/InlineSpinner";
import { CharCounter, ErrorPage, InlineError, LoadingPage, StudioShell } from "../components/StudioShell";
import { TeacherModal } from "../components/TeacherChrome";
import { ApiError, apiRequest, useWork } from "../lib/api";
import { useAutosave } from "../lib/autosave";
import { navigate } from "../lib/navigation";
import { pathForStep } from "../lib/session";

export function StoryboardPage({ workId }: { workId: string }) {
  const query = useWork(workId);
  if (query.loading) return <LoadingPage label="스토리보드를 불러오는 중..." />;
  if (!query.data || query.error) {
    return <ErrorPage message={query.error?.message ?? "스토리보드를 불러오지 못했습니다."} />;
  }
  return <StoryboardEditor workId={workId} initial={query.data} />;
}

function StoryboardEditor({
  workId,
  initial,
}: {
  workId: string;
  initial: WorkResponse;
}) {
  const [story, setStory] = useState(initial.work.story_line ?? "");
  const [cutCount, setCutCount] = useState<4 | 5 | 6>(
    initial.cuts.length === 4 || initial.cuts.length === 5 || initial.cuts.length === 6
      ? initial.cuts.length
      : 4,
  );
  const [cuts, setCuts] = useState<Cut[]>(initial.cuts);
  const [splitting, setSplitting] = useState(false);
  const [moving, setMoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manualHint, setManualHint] = useState(false);
  const [shrinkConfirmOpen, setShrinkConfirmOpen] = useState(false);
  const splitTriggerRef = useRef<HTMLButtonElement | null>(null);
  const payload = useMemo(() => ({
    step: "story" as const,
    story_line: story,
    scene_text: cuts.map((cut) => ({ id: cut.id, scene_text: cut.scene_text })),
  }), [cuts, story]);
  const autosave = useAutosave(workId, payload);

  const split = async (confirmShrink = false): Promise<void> => {
    if (!story.trim()) {
      setError("한 줄 이야기를 먼저 적어 주세요.");
      return;
    }
    setSplitting(true);
    setError(null);
    setManualHint(false);
    try {
      await autosave.flush(payload);
      const result = await apiRequest<{ cuts: Cut[] }>(
        `/api/works/${encodeURIComponent(workId)}/cuts/split`,
        {
          method: "POST",
          body: JSON.stringify({ cut_count: cutCount, confirm_shrink: confirmShrink }),
        },
      );
      setCuts(result.cuts);
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === "CUTS_HAVE_IMAGES" && !confirmShrink) {
        setShrinkConfirmOpen(true);
      } else {
        setError(caught instanceof ApiError ? caught.message : "AI가 컷을 나누지 못했어요.");
        setManualHint(true);
      }
    } finally {
      setSplitting(false);
    }
  };

  const next = async (): Promise<void> => {
    if (cuts.length === 0 || cuts.some((cut) => !cut.scene_text.trim())) {
      setError("모든 컷의 장면을 채워 주세요.");
      return;
    }
    setMoving(true);
    setError(null);
    try {
      await autosave.flush({ ...payload, step: "cuts" });
      navigate(pathForStep(workId, "cuts"));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "스토리보드를 저장하지 못했습니다.");
    } finally {
      setMoving(false);
    }
  };

  return (
    <StudioShell
      currentStep={2}
      imageRegenRemaining={initial.image_regen_remaining}
      dirty={story !== (initial.work.story_line ?? "") || JSON.stringify(cuts) !== JSON.stringify(initial.cuts)}
      autosaveStatus={autosave.status}
    >
      <div className="mv-page">
        <header className="mv-heading">
          <span className="mv-kicker">MV 2 · 스토리보드</span>
          <h1 tabIndex={-1}>노래에 어울리는 이야기를 나눠요</h1>
          <p>한 줄 이야기를 적으면 AI가 컷별 장면을 제안해요. 마음대로 고쳐도 좋아요.</p>
        </header>
        <section className="mv-card storyboard-brief">
          <label htmlFor="story-line">한 줄 이야기</label>
          <textarea
            id="story-line"
            value={story}
            maxLength={1_000}
            placeholder="예: 작은 별이 친구들을 만나 밤하늘 축제를 여는 이야기"
            onChange={(event) => setStory(event.target.value)}
          />
          <CharCounter value={story} max={1_000} />
          <div className="storyboard-controls">
            <fieldset>
              <legend>컷 수</legend>
              {[4, 5, 6].map((count) => (
                <button
                  type="button"
                  className={cutCount === count ? "selected" : ""}
                  aria-pressed={cutCount === count}
                  key={count}
                  onClick={() => setCutCount(count as 4 | 5 | 6)}
                >{count}컷</button>
              ))}
            </fieldset>
            <button ref={splitTriggerRef} className="button-primary" type="button" disabled={splitting} onClick={() => void split()}>
              {splitting ? <InlineSpinner label="장면을 나누는 중..." /> : "AI로 컷 나누기"}
            </button>
          </div>
          <aside className="tip-card">
            <strong>좋은 컷 팁</strong>
            <ul>
              <li>컷 하나 = <b>장면 하나</b> — 장소·행동이 그려지게 써요</li>
              <li>노래의 후렴에 가장 큰 장면을 놓아요</li>
              <li>AI 제안은 초안일 뿐 — 컷마다 내 말로 고쳐요</li>
            </ul>
          </aside>
          {manualHint ? <p className="manual-hint">직접 컷을 나눠 써도 좋아요. 기존 장면이 있다면 아래에서 바로 고쳐 주세요.</p> : null}
        </section>
        {cuts.length > 0 ? (
          <section className="storyboard-list" aria-label="컷별 장면">
            {cuts.map((cut) => (
              <article className="storyboard-row" key={cut.id}>
                <b>{cut.idx}</b>
                <label>
                  <span>컷 {cut.idx} 장면</span>
                  <textarea
                    value={cut.scene_text}
                    maxLength={1_000}
                    onChange={(event) => setCuts((current) => current.map((item) =>
                      item.id === cut.id ? { ...item, scene_text: event.target.value } : item
                    ))}
                  />
                </label>
              </article>
            ))}
          </section>
        ) : <p className="storyboard-empty">한 줄 이야기를 적고 AI로 컷을 나눠 보세요.</p>}
        {error ? <InlineError>{error}</InlineError> : null}
        <footer className="mv-footer">
          <button className="button-secondary" type="button" onClick={() => navigate(pathForStep(workId, "song"))}>이전</button>
          <button className="button-primary" type="button" disabled={moving || cuts.length === 0 || cuts.some((cut) => !cut.scene_text.trim())} onClick={() => void next()}>
            {moving ? <InlineSpinner label="저장 중..." /> : "컷 만들기로"}
          </button>
        </footer>
        {shrinkConfirmOpen ? (
          <TeacherModal
            title="컷 수를 줄일까요?"
            destructive
            returnFocusRef={splitTriggerRef}
            onClose={() => setShrinkConfirmOpen(false)}
            actions={(
              <>
                <button className="button-secondary" type="button" data-teacher-modal-cancel onClick={() => setShrinkConfirmOpen(false)}>취소</button>
                <button className="button-danger" type="button" onClick={() => {
                  setShrinkConfirmOpen(false);
                  void split(true);
                }}>컷 수 줄이기</button>
              </>
            )}
          >
            <p>그림이 있는 컷이 사라져요. 그래도 컷 수를 줄일까요?</p>
          </TeacherModal>
        ) : null}
      </div>
    </StudioShell>
  );
}
